import { countResumoDg } from './resumoDgCounts'

const UNKNOWN_GROUP = 'Não informado'

export const ECC_CATEGORIES = [
  '<2,5',
  '2,75',
  '3',
  '3,25',
  '3,5',
  '>3,5',
  UNKNOWN_GROUP,
]

export const parseNumericValue = (value) => {
  const normalized = String(value ?? '')
    .replace(',', '.')
    .match(/-?\d+(?:\.\d+)?/)
  return normalized ? Number(normalized[0]) : null
}

export const getEccCategory = (value) => {
  const numeric = parseNumericValue(value)
  if (numeric === null) return UNKNOWN_GROUP
  // O domínio "ECC" vai de 1 a 5 em passos de 0,25 (visto em syncData.json). O corte de
  // "<2,5" precisa incluir o próprio 2,5 — senão essa nota real cai num buraco entre os
  // dois primeiros grupos e vira "Não informado" em silêncio.
  if (numeric <= 2.5) return '<2,5'
  if (numeric > 3.5) return '>3,5'
  return [2.75, 3, 3.25, 3.5].includes(numeric)
    ? String(numeric).replace('.', ',')
    : UNKNOWN_GROUP
}

const getGroupLabel = (value) =>
  value === null || value === undefined || value === ''
    ? UNKNOWN_GROUP
    : String(value)

const groupRows = (rows, getLabel) => {
  const groups = new Map()
  rows.forEach((row) => {
    const label = getGroupLabel(getLabel(row))
    if (!groups.has(label)) groups.set(label, [])
    groups.get(label).push(row)
  })

  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right, 'pt-BR'))
    .map(([group, rowsInGroup]) => ({
      group,
      ...countResumoDg(rowsInGroup),
    }))
}

const groupRowsInOrder = (rows, getLabel, order) => {
  const grouped = groupRows(rows, getLabel)
  const positions = new Map(order.map((label, index) => [label, index]))
  return grouped.sort(
    (left, right) =>
      (positions.get(left.group) ?? order.length) -
        (positions.get(right.group) ?? order.length) ||
      left.group.localeCompare(right.group, 'pt-BR'),
  )
}

export const buildResumoDgAnalyses = (rows = []) => ({
  general: {
    key: 'general',
    label: 'Geral',
    rows: [{ group: 'Lote', ...countResumoDg(rows) }],
  },
  order: {
    key: 'order',
    label: 'Por Ordem',
    rows: groupRows(rows, (row) => row.ordemMatriz),
  },
  age: {
    key: 'age',
    label: 'Por Idade',
    rows: groupRows(rows, (row) => row.idadeMatriz),
  },
  ecc: {
    key: 'ecc',
    label: 'Por Escore (ECC)',
    rows: groupRowsInOrder(rows, (row) => getEccCategory(row.ecc), [
      '<2,5',
      '2,75',
      '3',
      '3,25',
      '3,5',
      '>3,5',
      UNKNOWN_GROUP,
    ]),
  },
  breed: {
    key: 'breed',
    label: 'Por Raça',
    rows: groupRows(rows, (row) => row.racaMatriz),
  },
  bull: {
    key: 'bull',
    label: 'Por Touro',
    rows: groupRows(rows, (row) => row.touro),
  },
})

export const analysisTotalsMatch = (analysis, totalStats) => {
  const totals = analysis.rows.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      saved: acc.saved + row.saved,
      pending: acc.pending + row.pending,
      pregnant: acc.pregnant + row.pregnant,
      empty: acc.empty + row.empty,
    }),
    { total: 0, saved: 0, pending: 0, pregnant: 0, empty: 0 },
  )

  return ['total', 'saved', 'pending', 'pregnant', 'empty'].every(
    (key) => totals[key] === totalStats[key],
  )
}

export const ANALYSIS_ORDER = ['general', 'order', 'breed', 'bull']
